import { useMemo, useState } from 'react';
import {
  useCentersOverview,
  useCreateCenter,
  useUpdateCenter,
  useDeleteCenter,
} from '@hooks/useAdminResources';
import { useConfirm } from '@hooks/useConfirm';
import { useAuth } from '@hooks/useAuth';
import type { Center, CenterWithCounts } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { CenterDrawer, CenterCreateModal } from './CenterDrawer';
import { CenterCard } from './CenterCard';

function InviteSentModal({ email, onClose }: { email: string; onClose: () => void }) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">Centro creado</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '12px 14px', background: 'var(--success-soft, oklch(0.96 0.04 150))', border: '1px solid oklch(0.85 0.07 150)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--fg)', marginBottom: 16 }}>
            Hemos enviado una invitación de acceso a <strong>{email}</strong>. El administrador
            entrará con el enlace del email — sin contraseña. El enlace caduca a los 30 minutos.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" data-variant="primary" onClick={onClose}>Entendido</button>
        </div>
      </div>
    </div>
  );
}

type DrawerState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; center: Center };

interface Totals {
  readonly activeCount: number;
  readonly inactiveCount: number;
  readonly total: number;
  readonly roomsTotal: number;
  readonly staffTotal: number;
  readonly bookingsTotal: number;
}

function computeTotals(centers: readonly CenterWithCounts[]): Totals {
  let activeCount = 0;
  let roomsTotal = 0;
  let staffTotal = 0;
  let bookingsTotal = 0;
  for (const c of centers) {
    if (c.isActive) activeCount += 1;
    roomsTotal += c.roomsCount;
    staffTotal += c.staffCount;
    bookingsTotal += c.bookingsLast30d;
  }
  return {
    activeCount,
    inactiveCount: centers.length - activeCount,
    total: centers.length,
    roomsTotal,
    staffTotal,
    bookingsTotal,
  };
}

export function CentersPage() {
  const confirm = useConfirm();
  const { hasRole } = useAuth();
  const isSuper = hasRole('superadmin');
  const { data, isLoading } = useCentersOverview();
  const createMutation = useCreateCenter();
  const updateMutation = useUpdateCenter();
  const deleteMutation = useDeleteCenter();

  const [drawer, setDrawer] = useState<DrawerState>({ mode: 'closed' });
  const [inviteSent, setInviteSent] = useState<{ email: string } | null>(null);

  const centers = data?.centers ?? [];
  const totals = useMemo(() => computeTotals(centers), [centers]);

  async function handleCreate(payload: Record<string, unknown>) {
    const { adminEmail, ...rest } = payload as Record<string, unknown> & { adminEmail?: string };
    await createMutation.mutateAsync({ ...rest, adminEmail });
    if (adminEmail) {
      setInviteSent({ email: adminEmail });
    }
    setDrawer({ mode: 'closed' });
  }

  async function handleEdit(payload: Record<string, unknown>) {
    if (drawer.mode !== 'edit') return;
    await updateMutation.mutateAsync({ id: drawer.center.id, data: payload });
    setDrawer({ mode: 'closed' });
  }

  async function handleDelete(c: Center) {
    const ok = await confirm({
      title: 'Eliminar centro',
      message: (
        <>
          ¿Eliminar el centro <strong>{c.name}</strong>? Se eliminarán todos sus servicios,
          staff y reservas. Esta acción no se puede deshacer.
        </>
      ),
      tone: 'danger',
      confirmLabel: 'Eliminar centro',
    });
    if (ok) {
      await deleteMutation.mutateAsync(c.id);
    }
  }

  function handleToggleActive(c: CenterWithCounts, next: boolean) {
    void updateMutation.mutateAsync({ id: c.id, data: { isActive: next } });
  }

  // The drawer state holds a plain `Center` (no counts) — drawer doesn't need
  // them. We strip the augmented fields when opening so the drawer's existing
  // form stays untouched.
  function openEdit(c: CenterWithCounts) {
    const { roomsCount: _r, staffCount: _s, bookingsLast30d: _b, ...base } = c;
    void _r; void _s; void _b;
    setDrawer({ mode: 'edit', center: base });
  }

  return (
    <div>
      <PageHead
        title="Centros"
        sub={isSuper
          ? 'Administra ubicaciones, datos de contacto y estado operativo.'
          : 'Tus centros. Como admin solo puedes gestionar Salas — el resto es solo lectura.'}
        meta={
          <span style={{ fontWeight: 600, color: 'var(--accent)' }} className="mono">
            {isSuper ? 'SUPERADMIN' : 'ADMIN'}
          </span>
        }
        actions={
          isSuper ? (
            <button className="btn" data-variant="primary" onClick={() => { setDrawer({ mode: 'create' }); }}>
              <Icons.Plus size={14} /> Nuevo centro
            </button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 64, color: 'var(--fg-muted)' }}>Cargando…</div>
      ) : (
        <>
          <div className="hero-stats">
            <div className="hero-stat" style={{ cursor: 'default' }}>
              <div className="hero-stat-head">
                <span className="hero-stat-label">Centros activos</span>
              </div>
              <div className="hero-stat-body">
                <span className="hero-stat-value mono">
                  {totals.activeCount}
                  <span style={{ color: 'var(--fg-muted)', fontSize: 16 }}> / {totals.total}</span>
                </span>
              </div>
              <div className="hero-stat-foot">
                {totals.inactiveCount} {totals.inactiveCount === 1 ? 'inactivo' : 'inactivos'}
              </div>
            </div>
            <div className="hero-stat" style={{ cursor: 'default' }}>
              <div className="hero-stat-head">
                <span className="hero-stat-label">Salas totales</span>
              </div>
              <div className="hero-stat-body">
                <span className="hero-stat-value mono">{totals.roomsTotal}</span>
              </div>
              <div className="hero-stat-foot">Capacidad simultánea</div>
            </div>
            <div className="hero-stat" style={{ cursor: 'default' }}>
              <div className="hero-stat-head">
                <span className="hero-stat-label">Profesionales asignados</span>
              </div>
              <div className="hero-stat-body">
                <span className="hero-stat-value mono">{totals.staffTotal}</span>
              </div>
              <div className="hero-stat-foot">En plantilla</div>
            </div>
            <div className="hero-stat" style={{ cursor: 'default' }}>
              <div className="hero-stat-head">
                <span className="hero-stat-label">Reservas / Mes</span>
              </div>
              <div className="hero-stat-body">
                <span className="hero-stat-value mono">{totals.bookingsTotal}</span>
              </div>
              <div className="hero-stat-foot">Últimos 30 días</div>
            </div>
          </div>

          <div className="centers-grid">
            {centers.map((c) => (
              <CenterCard
                key={c.id}
                center={c}
                onToggle={(next) => { handleToggleActive(c, next); }}
                onOpen={() => { openEdit(c); }}
                toggleDisabled={updateMutation.isPending || !isSuper}
              />
            ))}
            {isSuper && (
              <button
                type="button"
                className="center-card-add"
                onClick={() => { setDrawer({ mode: 'create' }); }}
                data-testid="center-card-add"
              >
                <Icons.Plus size={20} />
                <span className="center-card-add-title">Añadir nuevo centro</span>
                <span className="center-card-add-sub">Configura ubicación, horarios y staff</span>
              </button>
            )}
          </div>
        </>
      )}

      {drawer.mode === 'create' && isSuper && (
        <CenterCreateModal
          onClose={() => { setDrawer({ mode: 'closed' }); }}
          onSave={handleCreate}
          isSaving={createMutation.isPending}
        />
      )}

      {drawer.mode === 'edit' && (
        <CenterDrawer
          center={drawer.center}
          onClose={() => { setDrawer({ mode: 'closed' }); }}
          onSave={handleEdit}
          isSaving={updateMutation.isPending}
          {...(isSuper ? { onDelete: () => { void handleDelete(drawer.center); } } : {})}
        />
      )}

      {inviteSent && (
        <InviteSentModal
          email={inviteSent.email}
          onClose={() => { setInviteSent(null); }}
        />
      )}

    </div>
  );
}
